import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 20, bottom: 50, left: 60 };
const HEIGHT = 400;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '12px',
    alignItems: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#374151',
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  container: {
    overflowX: 'auto',
    minWidth: '400px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '240px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

export default function CompetitivePositionChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(400, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;

    svg
      .attr('width', '100%')
      .attr('height', HEIGHT)
      .attr('viewBox', `0 0 ${width} ${HEIGHT}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Scales
    const xExtent = d3.extent(data, (d) => d.avgPrice);
    const yExtent = d3.extent(data, (d) => d.changePercent);
    const xPad = (xExtent[1] - xExtent[0]) * 0.15 || 5;
    const yPad = (yExtent[1] - yExtent[0]) * 0.15 || 2;

    const x = d3.scaleLinear()
      .domain([xExtent[0] - xPad, xExtent[1] + xPad])
      .nice()
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([yExtent[0] - yPad, yExtent[1] + yPad])
      .nice()
      .range([innerH, 0]);

    // Bubble size scale based on addressCount
    const countExtent = d3.extent(data, (d) => d.addressCount);
    const sizeScale = d3.scaleSqrt()
      .domain([countExtent[0] || 1, countExtent[1] || 1])
      .range([8, 35])
      .clamp(true);

    // Volatility color scale (low=green, mid=blue, high=red)
    const volExtent = d3.extent(data, (d) => d.volatility);
    const colorScale = d3.scaleLinear()
      .domain([volExtent[0] || 0, (volExtent[0] + volExtent[1]) / 2 || 1, volExtent[1] || 2])
      .range(['#16a34a', '#2563eb', '#dc2626'])
      .clamp(true);

    // Mean avg price for vertical quadrant divider
    const meanPrice = d3.mean(data, (d) => d.avgPrice);

    // Grid lines - horizontal
    g.selectAll('.grid-h')
      .data(y.ticks())
      .enter()
      .append('line')
      .attr('class', 'grid-h')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Grid lines - vertical
    g.selectAll('.grid-v')
      .data(x.ticks())
      .enter()
      .append('line')
      .attr('class', 'grid-v')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Quadrant divider - vertical dashed line at mean price
    g.append('line')
      .attr('x1', x(meanPrice))
      .attr('x2', x(meanPrice))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,4');

    // Quadrant divider - horizontal dashed line at y=0
    if (y.domain()[0] <= 0 && y.domain()[1] >= 0) {
      g.append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', y(0))
        .attr('y2', y(0))
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '6,4');
    }

    // Quadrant labels
    const quadrants = [
      { label: 'Cheap & Rising', xPos: 8, yPos: 16, anchor: 'start', color: '#16a34a' },
      { label: 'Expensive & Rising', xPos: innerW - 8, yPos: 16, anchor: 'end', color: '#f59e0b' },
      { label: 'Cheap & Falling', xPos: 8, yPos: innerH - 8, anchor: 'start', color: '#6b7280' },
      { label: 'Expensive & Falling', xPos: innerW - 8, yPos: innerH - 8, anchor: 'end', color: '#dc2626' },
    ];

    quadrants.forEach((q) => {
      g.append('text')
        .attr('x', q.xPos)
        .attr('y', q.yPos)
        .attr('text-anchor', q.anchor)
        .attr('font-size', '11px')
        .attr('font-weight', 500)
        .attr('fill', q.color)
        .attr('opacity', 0.3)
        .text(q.label);
    });

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(8)
          .tickSize(0)
          .tickFormat((d) => `$${d}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // X axis label
    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 40)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Average Price ($)');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(8)
          .tickSize(0)
          .tickFormat((d) => (d > 0 ? `+${d}%` : `${d}%`))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Price Change (%)');

    // Sort data so smaller bubbles render on top (larger ones first)
    const sortedData = [...data].sort((a, b) => b.addressCount - a.addressCount);

    // Draw bubbles
    sortedData.forEach((d) => {
      const cx = x(d.avgPrice);
      const cy = y(d.changePercent);
      const r = sizeScale(d.addressCount);
      const fill = colorScale(d.volatility);

      // Bubble
      g.append('circle')
        .attr('cx', cx)
        .attr('cy', cy)
        .attr('r', r)
        .attr('fill', fill)
        .attr('opacity', 0.7)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5)
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          d3.select(event.currentTarget)
            .attr('opacity', 1)
            .attr('stroke-width', 2.5);
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            item: d,
            color: fill,
          });
        })
        .on('mouseleave', (event) => {
          d3.select(event.currentTarget)
            .attr('opacity', 0.7)
            .attr('stroke-width', 1.5);
          setTooltip(null);
        });

      // State label inside or next to bubble
      g.append('text')
        .attr('x', cx)
        .attr('y', cy)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('font-size', r >= 14 ? '10px' : '9px')
        .attr('font-weight', 600)
        .attr('fill', r >= 14 ? '#fff' : '#374151')
        .attr('pointer-events', 'none')
        .text(d.state);
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Competitive Position Map</div>
        <div style={styles.subtitle}>State positioning by price level vs price momentum</div>
        <div style={styles.emptyState}>No competitive position data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Competitive Position Map</div>
      <div style={styles.subtitle}>State positioning by price level vs price momentum</div>
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendDot, background: '#16a34a' }} />
          <span>Low Volatility</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendDot, background: '#2563eb' }} />
          <span>Mid Volatility</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendDot, background: '#dc2626' }} />
          <span>High Volatility</span>
        </div>
        <div style={{ ...styles.legendItem, marginLeft: '8px', color: '#9ca3af', fontSize: '11px' }}>
          Bubble size = address count
        </div>
      </div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.item.state}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Avg Price</span>
            <span style={{ fontWeight: 600 }}>${tooltip.item.avgPrice.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Price Change</span>
            <span style={{ fontWeight: 600, color: tooltip.item.changePercent >= 0 ? '#16a34a' : '#dc2626' }}>
              {tooltip.item.changePercent >= 0 ? '+' : ''}{tooltip.item.changePercent.toFixed(2)}%
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Addresses</span>
            <span style={{ fontWeight: 600 }}>{tooltip.item.addressCount}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#6b7280' }}>Volatility</span>
            <span style={{ fontWeight: 600, color: tooltip.color }}>{tooltip.item.volatility.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
